// ================================================================ //
// -- Functional Components (exclusive to FLUID Empty State Container )
//

import {
  FluidAction,
  FluidColor,
  FluidContainerElementType,
  FluidEmptyStateTranslationOptions,
  FluidIconConfig,
  FluidSize,
} from '@lmig/fluid-core';
import { h } from '@stencil/core';

import { FluidI18nStore } from '../../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { Utils } from '../../../utils/utils';
import { FluidImageConfig } from '../../fluid-image/components/fluid-image.components';

function isEmptyPage(type: FluidContainerElementType) {
  return type === FluidContainerElementType.PAGE;
}
export function EmptyStateTitle(props: {
  titleText: string;
  type: FluidContainerElementType;
  iconConfig?: FluidIconConfig;
  hasImage?: boolean;
  fluidTranslate?: FluidEmptyStateTranslationOptions;
  i18nStore?: FluidI18nStore;
}) {
  const { titleText, type, iconConfig, hasImage, i18nStore, fluidTranslate } =
    props;

  const translatedTitleText: string =
    i18nStore?.translate(fluidTranslate?.titleText) || titleText;
  return hasImage && iconConfig ? (
    <EmptyStateTitleWithIcon
      iconConfig={iconConfig}
      type={type}
      titleText={titleText}
      i18nStore={i18nStore}
      fluidTranslate={fluidTranslate}
    />
  ) : (
    translatedTitleText && (
      <div class={'empty-state-title'}>
        <fluid-title
          level={isEmptyPage(type) ? '1' : '3'}
          color={FluidColor.GRAY}>
          {translatedTitleText}
        </fluid-title>
      </div>
    )
  );
}

function EmptyStateTitleWithIcon(props: {
  titleText: string;
  type: FluidContainerElementType;
  iconConfig?: FluidIconConfig;
  fluidTranslate?: FluidEmptyStateTranslationOptions;
  i18nStore?: FluidI18nStore;
}) {
  const { titleText, type, iconConfig, i18nStore, fluidTranslate } = props;
  const translatedTitleText: string =
    i18nStore?.translate(fluidTranslate?.titleText) || titleText;

  return (
    translatedTitleText && (
      <div class={'empty-state-title with-icon'}>
        <fluid-title
          class={'empty-state-title-text'}
          level={isEmptyPage(type) ? '1' : '3'}
          color={FluidColor.GRAY}>
          {translatedTitleText}
        </fluid-title>
        <fluid-icon
          class={'empty-state-title-icon'}
          config={iconConfig}
          size={isEmptyPage(type) ? FluidSize.LARGE : FluidSize.MEDIUM}
        />
      </div>
    )
  );
}

export function EmptyStateBody(props: {
  bodyText: string;
  fluidTranslate?: FluidEmptyStateTranslationOptions;
  i18nStore?: FluidI18nStore;
}) {
  const { bodyText, fluidTranslate, i18nStore } = props;
  const translatedBodyText: string =
    i18nStore?.translate(fluidTranslate?.bodyText) || bodyText;

  return (
    translatedBodyText && (
      <div class={'empty-state-body'} innerHTML={translatedBodyText}></div>
    )
  );
}

export function EmptyStateImage(props: {
  imageConfig: FluidImageConfig;
  type: FluidContainerElementType;
}) {
  const { imageConfig, type } = props;
  const style = {
    ['empty-state-image']: true,
    [`empty-state-${type}`]: true,
  };
  return (
    !!imageConfig && (
      <div class={style}>
        <fluid-image config={imageConfig} inline={isEmptyPage(type)} />
      </div>
    )
  );
}

export function EmptyStateIcon(props: {
  iconConfig: FluidIconConfig;
  type: FluidContainerElementType;
  hasImage?: boolean;
}) {
  const { iconConfig, type, hasImage } = props;
  const style = {
    ['empty-state-icon']: true,
    [`empty-state-${type}`]: true,
  };

  const doRender =
    !!iconConfig &&
    !Utils.isEmptyObject(iconConfig) &&
    iconConfig?.key &&
    !hasImage;

  return (
    doRender && (
      <div class={style}>
        <fluid-icon
          size={FluidSize.EXTRA_LARGE}
          color={FluidColor.GRAY}
          config={iconConfig}
        />
      </div>
    )
  );
}

/**
 * Renders the actions the user can take when they encounter an empty state.
 * @param props
 * @constructor
 */
export function EmptyStateActions(props: {
  actions: FluidAction[];
  handleActionClicked: (action: FluidAction) => void;
}) {
  const { actions, handleActionClicked } = props;
  return (
    <div class={'empty-state-actions-container'}>
      {actions?.map((action: FluidAction, idx: number) => (
        <fluid-button
          removeHorizontalMargin={idx === actions?.length - 1}
          config={action}
          onClick={() => handleActionClicked(action)}
        />
      ))}
    </div>
  );
}
